import { logService, LOG_CATEGORIES } from '@/services/logService'

const RAW_API_BASE_URL = import.meta.env.DEV
  ? '/api/v1'
  : import.meta.env.VITE_API_BASE_URL || '/api/v1'
const EXPLICIT_API_ORIGIN = import.meta.env.VITE_API_ORIGIN || ''

const HTTP_URL_PATTERN = /^https?:\/\//i
const INTERNAL_API_PREFIX = '/api/v1/'
const PUBLIC_API_PREFIX = '/v1/'

let cachedOrigin: string | null | undefined
let cachedBaseUrl: string | null | undefined
let warnedAboutRelativeBase = false

const isHttpUrl = (value: string | null | undefined): value is string =>
  typeof value === 'string' && HTTP_URL_PATTERN.test(value)

const normalizeOrigin = (value: string): string => {
  try {
    const url = new URL(value)
    return url.origin
  } catch (error) {
    logService.warn(LOG_CATEGORIES.AUTH, 'Failed to normalize API origin', { value, error })
    return value
  }
}

const joinWithOrigin = (origin: string, path: string): string | null => {
  if (!origin) {
    return null
  }

  try {
    const normalizedOrigin = origin.endsWith('/') ? origin : `${origin}/`
    const normalizedPath = path.startsWith('/') ? path : `/${path}`
    return new URL(normalizedPath, normalizedOrigin)
      .toString()
      .replace(/\/+$/, (match) => (path.endsWith('/') ? match : match.slice(0, -1)))
  } catch (error) {
    logService.warn(LOG_CATEGORIES.AUTH, 'Failed to join API origin with path', {
      origin,
      path,
      error,
    })
    return null
  }
}

const computeApiOrigin = (): string | null => {
  if (cachedOrigin !== undefined) {
    return cachedOrigin
  }

  if (isHttpUrl(EXPLICIT_API_ORIGIN)) {
    cachedOrigin = normalizeOrigin(EXPLICIT_API_ORIGIN)
    return cachedOrigin
  }

  if (isHttpUrl(RAW_API_BASE_URL)) {
    cachedOrigin = new URL(RAW_API_BASE_URL).origin
    return cachedOrigin
  }

  if (typeof window !== 'undefined' && isHttpUrl(window.location.origin)) {
    cachedOrigin = window.location.origin
    return cachedOrigin
  }

  cachedOrigin = null
  return cachedOrigin
}

const computeApiBaseUrl = (): string | null => {
  if (cachedBaseUrl !== undefined) {
    return cachedBaseUrl
  }

  if (isHttpUrl(RAW_API_BASE_URL)) {
    cachedBaseUrl = RAW_API_BASE_URL
    return cachedBaseUrl
  }

  const origin = computeApiOrigin()
  if (origin) {
    cachedBaseUrl = joinWithOrigin(origin, RAW_API_BASE_URL)
    return cachedBaseUrl
  }

  cachedBaseUrl = RAW_API_BASE_URL

  if (!warnedAboutRelativeBase) {
    warnedAboutRelativeBase = true
    logService.warn(
      LOG_CATEGORIES.AUTH,
      'API base URL is relative and no HTTP origin is available; native downloads may fail',
      {
        rawBase: RAW_API_BASE_URL,
      },
    )
  }

  return cachedBaseUrl
}

export const resolveApiBaseUrl = (): string | null => computeApiBaseUrl()

export const resolveApiResourceUrl = (url: string | null | undefined): string | null => {
  if (!url) {
    return null
  }

  // When RAW_API_BASE_URL is a relative path (/api/v1) the Vite proxy is active.
  // Route all API resource URLs through the proxy at /api/v1 so they work
  // regardless of which origin the browser is using (localhost, LAN IP, etc.).
  if (!isHttpUrl(RAW_API_BASE_URL)) {
    // Absolute localhost URLs come from the backend embedding its own origin.
    // Strip the origin and normalise the path to /api/v1/... for the proxy.
    // Uses a regex instead of new URL() to avoid jsdom quirks where the URL
    // constructor resolves all URLs relative to the page origin.
    if (HTTP_URL_PATTERN.test(url)) {
      const localMatch =
        /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(\/[^?#]*)?(\?[^#]*)?/i.exec(url)
      if (localMatch) {
        const pathname = localMatch[1] || '/'
        const search = localMatch[2] || ''
        const path = pathname.startsWith(PUBLIC_API_PREFIX)
          ? INTERNAL_API_PREFIX + pathname.slice(PUBLIC_API_PREFIX.length)
          : pathname
        return path + search
      }
      // Non-local absolute URL (CDN, signed storage URL, etc.) — leave unchanged
      return url
    }

    // Relative /api/v1/... paths are already correct for the proxy — return verbatim
    if (url.startsWith(INTERNAL_API_PREFIX)) {
      return url
    }

    // Relative /v1/... paths (public API format) — normalise to /api/v1/...
    if (url.startsWith(PUBLIC_API_PREFIX)) {
      return INTERNAL_API_PREFIX + url.slice(PUBLIC_API_PREFIX.length)
    }
    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
      return url
    }

    // Native package snapshots store API-relative resource descriptors without an environment-
    // specific /v1 prefix so the same bytes work in production and local development.
    return `${INTERNAL_API_PREFIX}${url.replace(/^\/+/, '')}`
  }

  // Production: relative paths are resolved against the configured API base URL.
  const rawUrl = url.startsWith(INTERNAL_API_PREFIX)
    ? PUBLIC_API_PREFIX + url.slice(INTERNAL_API_PREFIX.length)
    : url

  if (HTTP_URL_PATTERN.test(rawUrl)) {
    return rawUrl
  }

  const baseUrl = resolveApiBaseUrl()
  if (baseUrl && isHttpUrl(baseUrl)) {
    try {
      return new URL(rawUrl, baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`).toString()
    } catch (error) {
      logService.warn(LOG_CATEGORIES.AUTH, 'Failed to resolve API resource URL', {
        baseUrl,
        url: rawUrl,
        error,
      })
      return rawUrl
    }
  }

  return rawUrl
}
